/**
 * Listing-card excerpt text — with the legacy article header stripped.
 *
 * THE DEFECT THIS FIXES (owner-reported 08/06/2026: "on some i see
 * Fridge.com in it for some reason or the header just repeated")
 *
 * 5,621 of 18,398 news articles — 31% — begin with a legacy header block
 * baked into `content`: the article title repeated as visible text, followed
 * by the site breadcrumb "Fridge.com | Blog". Measured live 08/06/2026.
 *
 * Every excerpt generator on the site strips tags and takes the first N
 * characters, so on a listing card the subheader under the title read:
 *
 *     Cheap 2 Door Refrigerator Fridge.com | Blog Exploring Budget-Friendly…
 *      ^^^^^^ the title, again        ^^^^^^^^^^^ site chrome
 *
 * — i.e. the card showed its own title twice and the brand name before it
 * ever reached a sentence about the subject.
 *
 * WHY THIS IS A DISPLAY-LAYER FIX AND NOT A DATA MIGRATION
 *
 * The boilerplate is inside `content`, which the article page renders as
 * structured HTML where it is invisible chrome, not stray text. Rewriting
 * 5,621 rows to fix a rendering artifact would be a large destructive edit
 * to source content for a problem that only exists when the HTML is
 * flattened to plain text. Stripping at the point of flattening fixes every
 * surface at once and cannot corrupt an article.
 *
 * The stripper is deliberately conservative: it only removes these fragments
 * when they sit at the START of the text, so an article that legitimately
 * mentions its own title or the brand mid-sentence is untouched.
 */

import { normalizeBrandSpelling } from './brand-spelling'

/** The site breadcrumb baked into legacy article bodies. */
const BREADCRUMB = /^fridge\.com\s*\|\s*blog\b/i

/** Collapse all whitespace runs so multi-line HTML text compares cleanly. */
function normalizeSpace(s: string): string {
  return s.replace(/\s+/g, ' ').trim()
}

/**
 * The forms a title can take in a legacy body header.
 *
 * Titles follow the binding sitewide formula "Target Keyword | Benefit or
 * Searcher's Goal | Brand Name", but the header baked into older bodies
 * usually carries only the FIRST segment — measured: the row titled
 * "Refrigerator Door Gasket Replacement | The Complete Step-by-Step Guide |
 * Fridge.com" opens its body with just "Refrigerator Door Gasket
 * Replacement". So three forms are matched: the full title, the title minus
 * the "| Fridge.com" suffix, and the leading segment alone.
 *
 * Longest first, so a fuller form is consumed before a shorter prefix of it.
 */
function titleVariants(title: string): string[] {
  const t = normalizeSpace(title)
  const withoutSuffix = t.replace(/\s*\|\s*fridge\.com\s*$/i, '').trim()
  const leadSegment = t.split('|')[0].trim()
  return Array.from(new Set([t, withoutSuffix, leadSegment]))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
}

function startsWithCI(haystack: string, needle: string): boolean {
  return haystack.slice(0, needle.length).toLowerCase() === needle.toLowerCase()
}

/**
 * Remove the legacy header block from the FRONT of already-flattened article
 * text. Loops because the two fragments appear in both orders across the
 * corpus, and a title can repeat twice.
 */
export function stripLegacyArticleHeader(text: string, title?: string | null): string {
  let out = normalizeSpace(text)
  const variants = title ? titleVariants(title) : []

  // Bounded: each pass must consume characters or the loop stops. The cap is
  // a backstop against a pathological input, never reached in practice.
  for (let pass = 0; pass < 6; pass++) {
    const before = out

    for (const v of variants) {
      if (v.length >= 3 && startsWithCI(out, v)) {
        out = out.slice(v.length).trim()
        // Legacy bodies separate the fragments with a stray pipe or dash.
        out = out.replace(/^[|\-–—·:]+\s*/, '')
      }
    }

    out = out.replace(BREADCRUMB, '').trim()
    out = out.replace(/^[|\-–—·:]+\s*/, '')

    if (out === before) break
  }

  return out
}

/**
 * The excerpt a listing card shows: HTML flattened, legacy header removed,
 * truncated on a word boundary with an ellipsis.
 *
 * Passing `title` is what enables the repetition strip — without it the
 * breadcrumb is still removed, but the duplicated title is not.
 */
export function buildExcerpt(
  html: string | null | undefined,
  title?: string | null,
  maxLength = 150
): string {
  const flattened = (html || '').replace(/<[^>]*>/g, ' ')
  const cleaned = normalizeBrandSpelling(stripLegacyArticleHeader(flattened, title))
  if (cleaned.length <= maxLength) return cleaned
  const cut = cleaned.slice(0, maxLength)
  const lastSpace = cut.lastIndexOf(' ')
  // Trim to a word boundary when one exists in the back half, so the excerpt
  // never ends mid-word.
  return (lastSpace > maxLength * 0.5 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…'
}
